// src/interaction/modes/PlaceIcon.ts
// ✨ ENHANCED WITH ICON REPLACEMENT CONFIRMATION
import { ModeActions } from 'src/types';
import { generateId, getItemAtTile, setWindowCursor } from 'src/utils';
import { VIEW_ITEM_DEFAULTS } from 'src/config';
import { iconPackManager } from '@/features/components/iconPacks';

// Store for managing icon replacement confirmation dialog
let iconReplacementPending: {
  existingItemId: string;
  newIconId: string;
  tile: { x: number; y: number };
} | null = null;

// Callback to trigger confirmation dialog
let showConfirmDialog: ((params: {
  existingItemId: string;
  newIconId: string;
  tile: { x: number; y: number };
}) => void) | null = null;

/**
 * Start the shared icon-replacement flow.
 *
 * Both pointer placement and native HTML drag/drop must use this entry point;
 * otherwise a sidebar drop on an occupied tile creates a second item instead
 * of opening the replacement confirmation dialog.
 */
export const requestIconReplacement = (params: {
  existingItemId: string;
  newIconId: string;
  tile: { x: number; y: number };
}) => {
  iconReplacementPending = params;
  showConfirmDialog?.(params);
};

export const PlaceIcon: ModeActions = {
  entry: () => {
    setWindowCursor('crosshair');
  },
  exit: () => {
    setWindowCursor('default');
    // Clear any pending replacement when exiting mode
    iconReplacementPending = null;
  },
  mousemove: () => {
    // Preview is handled by IconPlacementPreview component
    // No action needed here
  },
  mousedown: ({ uiState, scene, isRendererInteraction }) => {
    // Handle canvas clicks when NO icon is selected for placement
    // This allows users to click canvas to exit PLACE_ICON mode
    if (uiState.mode.type !== 'PLACE_ICON' || !isRendererInteraction) return;

    // If no icon ID, user clicked canvas without selecting an icon
    // Check if they clicked on an existing item and exit PLACE_ICON mode
    if (!uiState.mode.id) {
      const itemAtTile = getItemAtTile({
        tile: uiState.mouse.position.tile,
        scene
      });

      // Exit PLACE_ICON mode and return to CURSOR mode
      uiState.actions.setMode({
        type: 'CURSOR',
        mousedownItem: itemAtTile,
        showCursor: true
      });

      // If item exists, it will be selected on mouseup in CURSOR mode
      if (!itemAtTile) {
        uiState.actions.setItemControls(null);
      }
    }
    // If icon ID exists, do nothing - let mouseup handle placement
  },
  mouseup: ({ uiState, scene, isRendererInteraction }) => {
    // CRITICAL FIX: Icon placement + selection happens on mouseup (drop event)
    if (uiState.mode.type !== 'PLACE_ICON' || !isRendererInteraction) return;

    // If we have an icon ID, check if dropping on existing icon or empty space
    if (uiState.mode.id) {
      // Get icon definition from IconPackManager
      const iconDef = iconPackManager.getIconById(uiState.mode.id);

      if (!iconDef) {
        console.warn(`Icon not found: ${uiState.mode.id}`);
        return;
      }

      // Check if dropping on an existing item
      const itemAtTile = getItemAtTile({
        tile: uiState.mouse.position.tile,
        scene
      });

      // ✨ NEW: If dropping on existing ITEM, show confirmation dialog
      if (itemAtTile?.type === 'ITEM') {
        requestIconReplacement({
          existingItemId: itemAtTile.id,
          newIconId: uiState.mode.id,
          tile: uiState.mouse.position.tile
        });

        return; // Wait for confirmation
      }

      // Otherwise, place new icon (existing behavior)
      const modelItemId = generateId();

      // Create model item with icon reference
      scene.createModelItem({
        id: modelItemId,
        name: iconDef.name || 'Untitled',
        icon: uiState.mode.id
      });

      // Create view item at the dropped tile position
      scene.createViewItem({
        ...VIEW_ITEM_DEFAULTS,
        id: modelItemId,
        tile: uiState.mouse.position.tile
      });

      // CRITICAL FIX: Select the placed icon AND switch to CURSOR mode
      // This matches the behavior of TextBox mode
      uiState.actions.setItemControls({
        type: 'ITEM',
        id: modelItemId
      });

      // Switch to CURSOR mode but keep the item selection
      uiState.actions.setMode({
        type: 'CURSOR',
        showCursor: true,
        mousedownItem: null // Clear mousedownItem since we're not dragging
      });
    }
    // If no icon ID, mousedown already handled exiting the mode
  }
};

// ✨ Export functions for managing confirmation dialog from React components

export const registerConfirmDialog = (
  callback: (params: { existingItemId: string; newIconId: string; tile: { x: number; y: number } }) => void
) => {
  showConfirmDialog = callback;
};

export const confirmIconReplacement = (
  scene: ReturnType<typeof import('src/hooks/useScene').useScene>,
  uiStateActions: any
) => {
  if (!iconReplacementPending) return;

  const { existingItemId, newIconId } = iconReplacementPending;

  // Update the model item's icon (preserves all connections!)
  scene.updateModelItem(existingItemId, { icon: newIconId });

  // Select the updated icon
  uiStateActions.setItemControls({
    type: 'ITEM',
    id: existingItemId
  });

  // Switch to CURSOR mode
  uiStateActions.setMode({
    type: 'CURSOR',
    showCursor: true,
    mousedownItem: null
  });

  // Clear pending replacement
  iconReplacementPending = null;

};

export const cancelIconReplacement = (uiStateActions: any) => {
  console.log('❌ Icon replacement cancelled', uiStateActions);

  // Just clear the pending state, stay in PLACE_ICON mode
  iconReplacementPending = null;

  // User can continue dragging or click elsewhere
};
